import { useQuery } from "@tanstack/react-query";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { aiWorkflowsService } from "@/services/aiWorkflowsService";
import { SAMPLE_WORKFLOWS } from "@/data/aiToolkit";
import { WORKFLOW_TEMPLATES } from "@/data/workflowTemplates";
import type { Workflow } from "@/types/aiToolkit";

interface WorkflowRunSelectorProps {
  value: string | null;
  onChange: (workflowId: string | null, workflow: Workflow | null) => void;
  integrationSource?: string;
  disabled?: boolean;
  helperText?: string;
}

function workflowMatchesIntegration(workflow: Workflow, integrationSource: string): boolean {
  const source = integrationSource.toLowerCase();
  const tagMatch = workflow.tags?.some((t) => t.toLowerCase().includes(source));
  const triggerMatch = workflow.trigger?.config?.eventType
    ?.toLowerCase()
    .includes(source);
  return Boolean(tagMatch || triggerMatch);
}

export function WorkflowRunSelector({
  value,
  onChange,
  integrationSource,
  disabled = false,
  helperText = "Run multi-step automation — includes actions like creating ClickUp tasks",
}: WorkflowRunSelectorProps) {
  const { data: userWorkflows = [] } = useQuery({
    queryKey: ["/api/ai-workflows", "selector"],
    queryFn: () => aiWorkflowsService.getWorkflows(),
  });

  const allWorkflows = [
    ...WORKFLOW_TEMPLATES,
    ...SAMPLE_WORKFLOWS,
    ...userWorkflows,
  ];

  const filtered = integrationSource
    ? allWorkflows.filter((w) => workflowMatchesIntegration(w, integrationSource))
    : allWorkflows;

  const unique = Array.from(new Map(filtered.map((w) => [w.id, w])).values());

  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">Workflow</Label>
      <Select
        value={value ?? "__none__"}
        disabled={disabled}
        onValueChange={(v) => {
          if (v === "__none__") {
            onChange(null, null);
            return;
          }
          const wf = unique.find((w) => w.id === v) ?? null;
          onChange(v, wf);
        }}
      >
        <SelectTrigger className="h-9">
          <SelectValue placeholder="Select workflow" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">No workflow</SelectItem>
          {unique.map((w) => (
            <SelectItem key={w.id} value={w.id}>
              {w.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {helperText && (
        <p className="text-xs text-muted-foreground">{helperText}</p>
      )}
    </div>
  );
}
